import { useMemo, type CSSProperties, type ReactElement } from "react";
import { chromeSurface, color, layout, space } from "@tokens/index";
import { ContentSwap } from "@primitives/ContentSwap";
import { Dialog } from "@primitives/overlay";
import { SkeletonBlock } from "@primitives/Skeleton";
import { Text } from "@primitives/Text";
import { useViewport } from "@primitives/viewport";
import type { TimelineEvent } from "../../../experience-shell/workflows/timeline";
import { groupByDay } from "../workflow-timeline/grouping";
import { TimelineRow } from "../workflow-timeline/TimelineRow";
import {
  DisclosureBanner,
  EmptyState,
  ErrorState,
  TIMELINE_SKELETON_ROW_HEIGHT,
} from "../workflow-timeline/TimelineStates";

/**
 * Demand-loaded queue audit surface. The experience shell owns the query;
 * this composition owns modal behaviour and the feed. The dialog keeps its
 * own compact header ("Timeline · {queue}"). The card hugs the feed up to
 * the viewport cap; the dialog body is the scroll region. While the first
 * load is pending the body shows a fixed-height skeleton of four rows, so
 * the first frame is complete and a short feed never opens onto empty space.
 */

export interface QueueTimelineDialogProps {
  readonly isOpen: boolean;
  readonly queueName: string;
  readonly events: readonly TimelineEvent[];
  /** No feed on hand yet. Keep `true` until the first response, including before the query is enabled. */
  readonly isLoading: boolean;
  readonly error?: Error | null;
  readonly truncated: boolean;
  readonly partial: boolean;
  readonly onClose: () => void;
}

const SKELETON_ROWS = 4;
const SKELETON_ROW_HEIGHT = TIMELINE_SKELETON_ROW_HEIGHT;

const hostStyle: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  minWidth: 0,
};

function feedPaddingStyle(compact: boolean): CSSProperties {
  return {
    paddingBlock: space[4],
    paddingInline: compact ? space[3] : space[4],
  };
}

const feedColumnStyle: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: space[4],
  width: "100%",
  maxWidth: layout.contentMaxWidth,
  marginInline: "auto",
  minWidth: 0,
};

// The day groups stack with the column's gap inside the swap layer.
const feedStackStyle: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: space[4],
};

const dayHeaderStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: space[3],
  paddingBlock: space[2],
  position: "sticky",
  top: 0,
  zIndex: 1,
  ...chromeSurface,
};

const dayDividerStyle: CSSProperties = {
  flex: 1,
  height: "1px",
  background: color.border.subtle,
};

const eventListStyle: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 0,
  listStyle: "none",
  padding: 0,
  margin: 0,
};

function FeedSkeleton(): ReactElement {
  return (
    <div
      role="status"
      aria-live="polite"
      aria-label="Loading timeline"
      aria-busy="true"
      style={{ display: "flex", flexDirection: "column", gap: space[2] }}
    >
      <SkeletonBlock width={72} height={10} style={{ marginBlock: space[2] }} />
      {Array.from({ length: SKELETON_ROWS }, (_, row) => (
        <div
          key={row}
          aria-hidden="true"
          style={{
            display: "flex",
            gap: space[3],
            alignItems: "center",
            height: SKELETON_ROW_HEIGHT,
            paddingInline: space[1],
            boxSizing: "border-box",
          }}
        >
          <SkeletonBlock width={32} height={32} radius="full" />
          <SkeletonBlock height={10} style={{ flex: 1, width: "auto" }} />
        </div>
      ))}
    </div>
  );
}

export function QueueTimelineDialog({
  isOpen,
  queueName,
  events,
  isLoading,
  error,
  truncated,
  partial,
  onClose,
}: QueueTimelineDialogProps): ReactElement {
  const { isCompact } = useViewport();
  const groups = useMemo(() => groupByDay(events), [events]);
  const hasError = error !== undefined && error !== null;

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      title={`Timeline \u00b7 ${queueName}`}
      size="xl"
      presentation="auto"
      bodyPadding="none"
      testId="queue-timeline-dialog"
    >
      <div style={hostStyle} role="region" aria-label="Queue audit timeline">
        <div style={feedPaddingStyle(isCompact)}>
          <div style={feedColumnStyle}>
            {(truncated || partial) && !isLoading && !hasError ? (
              <DisclosureBanner truncated={truncated} partial={partial} subjectKind="queue" />
            ) : null}

            <ContentSwap
              ready={!isLoading}
              surface="surface"
              skeleton={<FeedSkeleton />}
              layerStyle={feedStackStyle}
            >
              {hasError ? (
                <ErrorState error={error} />
              ) : events.length === 0 ? (
                <EmptyState subjectKind="queue" />
              ) : (
                groups.map((group) => (
                  <section key={group.key} aria-label={group.label}>
                    <div style={dayHeaderStyle}>
                      <Text role="caps" size="xs" tone="muted">
                        {group.label}
                      </Text>
                      <span style={dayDividerStyle} />
                    </div>
                    <ul style={eventListStyle}>
                      {group.events.map((event) => (
                        <li key={event.activityId}>
                          <TimelineRow event={event} />
                        </li>
                      ))}
                    </ul>
                  </section>
                ))
              )}
            </ContentSwap>
          </div>
        </div>
      </div>
    </Dialog>
  );
}
